import { useCallback, useEffect, useState } from "react";

const SYNC_ENDPOINT = "/backend/auto_sync_capacity.php";
const DEFAULT_DELAY_MS = 1000;

/**
 * Ask the backend to recalculate slot capacities shortly after a page mounts.
 * @param {boolean} enabled - whether to run the sync at all
 * @param {number} delay - milliseconds to wait before calling the backend
 */
export const useCapacitySync = (enabled = true, delay = DEFAULT_DELAY_MS) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const syncCapacity = useCallback(async () => {
    setIsSyncing(true);
    setSyncResult(null);
    const timestamp = new Date().toISOString();
    try {
      const response = await fetch(SYNC_ENDPOINT, { headers: { "Content-Type": "application/json" } });
      const data = await response.json();
      setSyncResult(
        data.success
          ? { success: true, message: data.message, totalUpdated: data.total_updated, timestamp }
          : { success: false, error: data.error, timestamp }
      );
    } catch (error) {
      setSyncResult({ success: false, error: error.message, timestamp });
    } finally {
      setIsSyncing(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const timeoutId = setTimeout(syncCapacity, delay);
    return () => clearTimeout(timeoutId);
  }, [enabled, delay, syncCapacity]);

  return { syncCapacity, isSyncing, syncResult };
};

export default useCapacitySync;
